import { CalendarOutlined, DeleteFilled, FileTextOutlined } from '@ant-design/icons'
import { useFormatter } from '@api/useFormatter'
import { usePayroll } from '@api/usePayroll'
import { addCommasToNumber, GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Input, Popover, Row, Table, Tooltip } from 'antd'
import moment from 'moment/moment'
import React, { useState } from 'react'

import { AmountCol, getCalculationDisplay } from '../../helpers'
import { AddSpecialPayForm } from './AddSpecialPayForm'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { PayrollLinkPopover } from '@/components/DriverManagement/Common/PayrollLinkPopover'

export function SpecialPayItems({ detail, setDetail, driver, startDate, driverId }) {
  const { formatCurrency } = useFormatter()
  const formattedUnit = formatCurrency(null, true)
  return (
    <div className='p-4' style={{ flex: 5 }}>
      <Horizontal className='mb-4' verticalCenter>
        <Texto appearance='medium' category='heading-small' className='flex-1'>
          SPECIAL PAY
        </Texto>
        <Texto category='h4' appearance='secondary' weight='bold'>
          {formattedUnit}
          {addCommasToNumber(detail?.special_total, 2)}
        </Texto>
      </Horizontal>

      <Table
        className='payroll-calculated-table'
        columns={SpecialColumns(startDate, setDetail, driverId, formattedUnit)}
        dataSource={detail?.special}
        pagination={false}
      />
      <AddSpecialPayForm
        detail={detail}
        setDetail={setDetail}
        driver={driver}
        startDate={startDate}
        driverId={driverId}
      />
    </div>
  )
}

function RejectControlCol({ record, startDate, driverId }) {
  const [showingPopover, setShowingPopover] = useState()

  const [reason, setReason] = useState('')
  const { rejectSpecialPayMutation, recalcSinglePayrollMutation } = usePayroll()
  const { mutateAsync, isPending } = rejectSpecialPayMutation()
  const { mutateAsync: recalcSingle } = recalcSinglePayrollMutation()

  const rejectSpecialPay = () => {
    setShowingPopover(false)
    mutateAsync({ special_pay_id: record.id, reason }).then(() =>
      recalcSingle({ driverId, date: moment.utc(startDate).toISOString() })
    )
  }

  const handleReasonChange = (e) => {
    setReason(e.target.value)
  }

  return (
    <Popover
      placement='left'
      content={
        <div>
          <Input onChange={handleReasonChange} />
          <div className='mt-3 special-pay-reject'>
            <GraviButton buttonText='Cancel' appearance='outline' onClick={() => setShowingPopover(false)} />
            <GraviButton
              error
              loading={isPending}
              appearance='outline'
              buttonText='Reject'
              disabled={!reason || reason === '' || reason.length < 5}
              className='reject-special-pay ml-2'
              onClick={rejectSpecialPay}
            />
          </div>
        </div>
      }
      title='Why do you want to reject this request?'
      trigger='click'
      visible={showingPopover}
    >
      <GraviButton
        appearance='outline'
        ghost
        loading={isPending}
        icon={<DeleteFilled style={{ color: 'var(--theme-error)' }} />}
        onClick={() => {
          setShowingPopover(true)
        }}
      />
    </Popover>
  )
}

export const SpecialColumns = (startDate, setDetail, driverId, formattedUnit) => {
  return [
    {
      title: 'TYPE',
      dataIndex: 'name',
      key: 'name',
      render: (value, row) => {
        return (
          <Horizontal verticalCenter gap={4}>
            <Texto>{row.event}</Texto>
            {row.rate_type === 'scheduled' && (
              <Tooltip title='Schedule Managed Payroll Item'>
                <CalendarOutlined />
              </Tooltip>
            )}
            <Texto className='ml-2' weight={'bold'}>
              {row.name}
            </Texto>
            {row.note && (
              <Popover title={<Texto category='h6'>Special Pay Note</Texto>} content={<Texto>{row.note}</Texto>}>
                <GraviButton
                  style={{ color: 'var(--theme-color-2)' }}
                  size='small'
                  icon={<FileTextOutlined size={16} className='mb-1' />}
                  appearance='text'
                />
              </Popover>
            )}
            {row.linked_fli_info && (
              <PayrollLinkPopover linkedInfo={row.linked_fli_info} timezone={row.timezone} type='accessorial' />
            )}
          </Horizontal>
        )
      },
    },
    {
      title: 'CALCULATION',
      dataIndex: 'value_uom',
      key: 'value_uom',
      width: 150,
      render: (value, row) => {
        return <span className='special-calculation'>{getCalculationDisplay(row, formattedUnit)}</span>
      },
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (value) => formatters.DATE(moment(value)),
    },
    {
      title: 'ORDER NUMBER',
      dataIndex: 'order_number',
      key: 'order_number',
      render: (value) => {
        return <Texto>{value}</Texto>
      },
    },
    {
      title: () => (
        <Tooltip title='Displays the selected site(s) in which this special pay item will display in the Accessorial Events section of the delivery ticket.'>
          <span>DISPLAY ON TICKET</span>
        </Tooltip>
      ),
      dataIndex: 'sites',
      render: (value) => {
        return (
          <Vertical>
            {value?.map((site) => (
              <Texto key={site.number} weight='bold'>
                {site.number}-{site.name}
              </Texto>
            ))}
          </Vertical>
        )
      },
    },
    {
      title: 'AMOUNT ($)',
      dataIndex: 'dollar',
      key: 'dollar',
      align: 'right',
      render: (value, row) => {
        return <AmountCol row={row} />
      },
    },
    {
      title: '',
      key: 'actions',
      render: (data) => {
        if (data.rate_type === 'scheduled') return null
        return <RejectControlCol record={data} startDate={startDate} driverId={driverId} />
      },
    },
  ]
}
